import {
  Box,
  Text,
  Heading,
  Badge,
  Button,
  Stack,
  Flex,
  AvatarGroup,
  Avatar,
} from "@chakra-ui/react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Tooltip as TooltipChakra } from "@chakra-ui/react";
import { useState } from "react";
import { formatDistanceToNow } from "date-fns";

const DealCard = ({ lead }) => {
  const navigate = useNavigate();
  const role = useSelector((store) => store.authentication.users.role);

  const handleViewDetails = () => {
    if (role === "admin") {
      navigate(`/deals/${lead._id}`);
    } else if (role === "company") {
      navigate(`/companydeals/${lead._id}`);
    }
  };

  const [hoveredIdEmployee, setHoveredIdEmployee] = useState(null);

  const handleHoverEmployee = (id) => {
    setHoveredIdEmployee(id);
  };

  const handleMouseLeaveEmployee = () => {
    setHoveredIdEmployee(null);
  };

  const priorityColors = {
    Low: "green.100",
    Medium: "orange.100",
    High: "red.100",
  };
  const priorityTextColors = {
    Low: "green.900",
    Medium: "orange.900",
    High: "red.700",
  };

  const paymentColors = {
    Paid: "green.100",
    Pending: "yellow.100",
    "Partially Paid": "orange.100",
    Overdue: "red.100",
  };
  const paymentTextColors = {
    Paid: "green.900",
    Pending: "yellow.900",
    "Partially Paid": "orange.900",
    Overdue: "red.700",
  };

  return (
    <Box
      p={6}
      borderRadius="2xl"
      bgGradient="linear(to-br, teal.200, green.300, teal.400)"
      boxShadow="lg"
      color="white"
      position="relative"
      overflow="hidden"
      _before={{
        content: `""`,
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        bg: "rgba(255, 255, 255, 0.1)",
        backdropFilter: "blur(15px)",
        zIndex: 0,
      }}
      _hover={{
        transform: "scale(1.03)",
        boxShadow: "md",
      }}
      transition="all 0.3s ease-in-out"
      onClick={handleViewDetails}
    >
      <Stack spacing={3} position="relative" zIndex={1}>
        <Flex justifyContent="space-between" alignItems="center">
          <Heading
            fontSize="xl"
            fontWeight="bold"
            letterSpacing="wide"
            color={"blackAlpha.700"}
          >
            {lead.title} | {lead.company_name}
          </Heading>
          <Flex justifyContent="center" alignItems="center">
            <TooltipChakra
              label={`Assigned To: ${lead.assigned_to
                .map((m) => m.sudo_name)
                .join(", ")}`}
              isOpen={hoveredIdEmployee === lead._id}
              placement="bottom"
              color="green.500"
            >
              <Flex
                onMouseEnter={() => handleHoverEmployee(lead._id)}
                onMouseLeave={handleMouseLeaveEmployee}
              >
                <AvatarGroup size="xs" max={4}>
                  {lead.assigned_to.map((member, idx) => (
                    <Avatar
                      key={idx}
                      name={member.sudo_name}
                      src={`https://develop-project-360.onrender.com/${member.path}`}
                    />
                  ))}
                </AvatarGroup>

                {lead.assigned_to.length > 1 && (
                  <Button
                    size="xs"
                    rounded="full"
                    fontWeight="bold"
                    bg="gray.300"
                    color="red"
                    fontSize={10}
                    ml={2}
                    onMouseEnter={() => handleHoverEmployee(lead._id)}
                    onMouseLeave={handleMouseLeaveEmployee}
                  >
                    +{lead.assigned_to.length - 1}
                  </Button>
                )}
              </Flex>
            </TooltipChakra>
          </Flex>
        </Flex>
        <Stack direction="row" spacing={2} flexWrap="wrap">
          <Badge
            textColor="black"
            variant="solid"
            px={3}
            py={1}
            borderRadius="full"
            fontSize="0.75rem"
            bg="whiteAlpha.300"
            textTransform={"capitalize"}
          >
            Client: {lead.client_name}
          </Badge>
          <Badge
            textColor="black"
            variant="solid"
            px={3}
            py={1}
            borderRadius="full"
            fontSize="0.75rem"
            bg="whiteAlpha.300"
            cursor="pointer"
            textTransform={"none"}
            onClick={() => window.open(`tel:${lead.client_phone}`, "_blank")}
          >
            {lead.client_phone}
          </Badge>
          <Badge
            textColor="black"
            variant="solid"
            px={3}
            py={1}
            borderRadius="full"
            fontSize="0.75rem"
            bg="whiteAlpha.300"
            cursor="pointer"
            textTransform={"none"}
            onClick={() => window.open(`mailto:${lead.client_email}`, "_blank")}
          >
            {lead.client_email}
          </Badge>
        </Stack>

        <Stack direction="row" spacing={2} flexWrap="wrap">
          <Badge
            textColor="black"
            variant="solid"
            px={3}
            py={1}
            borderRadius="full"
            fontSize="0.75rem"
            bg="whiteAlpha.300"
            textTransform={"capitalize"}
          >
            Value: {lead.deal_value}
          </Badge>
          <Badge
            textColor={
              paymentTextColors[lead.payment_status] || "whiteAlpha.300"
            }
            variant="solid"
            px={3}
            py={1}
            borderRadius="full"
            fontSize="0.75rem"
            bg={paymentColors[lead.payment_status] || "whiteAlpha.300"}
            textTransform={"capitalize"}
          >
            Payment: {lead.payment_status}
          </Badge>
          {lead.payment_terms && (
            <Badge
              textColor="black"
              variant="solid"
              px={3}
              py={1}
              borderRadius="full"
              fontSize="0.75rem"
              bg="whiteAlpha.300"
              textTransform={"capitalize"}
            >
              Terms: {lead.payment_terms}
            </Badge>
          )}
        </Stack>
        <Stack direction="row" spacing={2} flexWrap="wrap">
          {lead.amount_paid > 0 && (
            <Badge
              textColor="green"
              variant="solid"
              px={3}
              py={1}
              borderRadius="full"
              fontSize="0.75rem"
              bg="whiteAlpha.300"
              textTransform={"capitalize"}
            >
              Paid: {lead.amount_paid}
            </Badge>
          )}

          {lead.remaining_payment > 0 && (
            <Badge
              textColor="red"
              variant="solid"
              px={3}
              py={1}
              borderRadius="full"
              fontSize="0.75rem"
              bg="whiteAlpha.300"
              textTransform={"capitalize"}
            >
              Remaining: {lead.remaining_payment}
            </Badge>
          )}
        </Stack>
        <Stack direction="row" spacing={2} flexWrap="wrap">
          <Badge
            textColor="black"
            variant="solid"
            px={3}
            py={1}
            borderRadius="full"
            fontSize="0.75rem"
            bg="whiteAlpha.300"
            textTransform={"capitalize"}
          >
            Stage: {lead.deal_stage}
          </Badge>
          <Badge
            textColor={priorityTextColors[lead.priority] || "whiteAlpha.300"}
            variant="solid"
            px={3}
            py={1}
            borderRadius="full"
            fontSize="0.75rem"
            bg={priorityColors[lead.priority] || "whiteAlpha.300"}
            textTransform={"capitalize"}
          >
            Priority: {lead.priority}
          </Badge>
          <Badge
            textColor="black"
            variant="solid"
            px={3}
            py={1}
            borderRadius="full"
            fontSize="0.75rem"
            bg="whiteAlpha.300"
            textTransform={"capitalize"}
          >
            Source: {lead.source}
          </Badge>
        </Stack>

        <Flex justifyContent={"space-between"}>
          {/* <Button
            size="sm"
            color="black"
            bg="whiteAlpha.300"
            _hover={{ bg: "whiteAlpha.500", color: "black" }}
            onClick={handleViewDetails}
          >
            View Details
          </Button> */}

          <Text fontSize="xs" color="whiteAlpha.900" mt={2}>
            Created{" "}
            {formatDistanceToNow(new Date(lead.createdAt), { addSuffix: true })}
          </Text>
        </Flex>
      </Stack>
    </Box>
  );
};

export default DealCard;
